import { useMemo, useState } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, differenceInDays } from 'date-fns';
import { Link } from 'react-router-dom';
import { parseLocalDate } from '../../lib/dateUtils';

// "Current Authorizations" for one client, AxisCare-style: one row per
// authorization bucket per week of the month, with % used and hours left.
// Authorized hours are the client's PA weekly hours (pa_skilled_hours_week,
// pa_pss_hours_week); used hours are that week's non-missed visits.

const svc = (v) => v.service?.code || v.service?.name || '';
export const isSkilledVisit = (v) => /rn|lpn|skilled|s912[34]/i.test(svc(v)) || v.service?.procedureCode === 'S9123' || v.service?.procedureCode === 'S9124';
export const isPssVisit = (v) => /pcs|fco|bha|personal|pss|unskilled|s9122/i.test(svc(v)) || v.service?.procedureCode === 'S9122';

export function paUrgency(client) {
  const expiry = client?.pa_expiry_date || client?.dma_6a_expiry_date;
  if (!expiry) return null;
  const days = differenceInDays(parseLocalDate(expiry), new Date());
  if (days < 0)   return { label: 'PA expired', tone: 'var(--danger)', bg: 'var(--danger-bg)', days };
  if (days <= 7)  return { label: `PA ${days}d left`, tone: 'var(--danger)', bg: 'var(--danger-bg)', days };
  if (days <= 30) return { label: `PA ${days}d left`, tone: 'var(--warn)', bg: 'var(--warn-bg)', days };
  return { label: `PA ${days}d left`, tone: 'var(--success)', bg: 'var(--success-bg)', days };
}

export default function AuthorizationsPanel({ client, visits, month }) {
  const [showPast, setShowPast] = useState(false);
  const skilledAuth = Number(client?.pa_skilled_hours_week) || 0;
  const pssAuth = Number(client?.pa_pss_hours_week) || 0;

  const rows = useMemo(() => {
    const out = [];
    const first = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
    const last = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
    const today = new Date();
    for (let ws = first; ws <= last; ws = addDays(ws, 7)) {
      const we = endOfWeek(ws, { weekStartsOn: 0 });
      const past = we < today;
      if (past && !showPast) continue;
      const inWeek = visits.filter(v => {
        const d = new Date(v.scheduledStartDate || v.startDate);
        return !isNaN(d) && d >= ws && d <= we && v._status !== 'Missed';
      });
      const buckets = [
        skilledAuth > 0 && { label: 'Skilled Nursing (RN / LPN)', auth: skilledAuth, used: inWeek.filter(isSkilledVisit).reduce((s, v) => s + v._hours, 0) },
        pssAuth > 0 && { label: 'Personal Care Support (CNA / FCO)', auth: pssAuth, used: inWeek.filter(isPssVisit).reduce((s, v) => s + v._hours, 0) },
      ].filter(Boolean);
      buckets.forEach(b => out.push({ ...b, ws, we, past, pct: Math.round((b.used / b.auth) * 100), left: b.auth - b.used }));
    }
    return out;
  }, [visits, month, skilledAuth, pssAuth, showPast]);

  const urg = paUrgency(client);
  const pctTone = (p) => p > 100 ? 'var(--danger)' : p >= 80 ? 'var(--warn)' : 'var(--success)';

  return (
    <div className="card" style={{ padding: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--border)', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Current Authorizations</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {urg && <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: urg.bg, color: urg.tone }}>{urg.label}</span>}
          <label style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={showPast} onChange={e => setShowPast(e.target.checked)} /> Show past weeks
          </label>
        </div>
      </div>
      <div style={{ padding: '6px 14px 10px', maxHeight: 360, overflowY: 'auto' }}>
        <div style={{ fontSize: 12, fontWeight: 600, margin: '6px 0 4px' }}>Weekly Authorizations</div>
        {skilledAuth === 0 && pssAuth === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0' }}>
            No weekly PA hours on this client's record yet. Add them under <Link to={client?.id ? `/prior-auth?client=${client.id}` : '/prior-auth'} style={{ color: 'var(--accent)' }}>Prior Auth</Link> or the client's Hours &amp; Auth tab and this panel fills in.
          </div>
        ) : rows.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0' }}>No remaining weeks this month. Tick "Show past weeks" to see them.</div>
        ) : rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', opacity: r.past ? 0.7 : 1 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500 }}>{r.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{format(r.ws, 'MMM d')} – {format(r.we, 'MMM d, yyyy')} · {r.auth}h authorized</div>
            </div>
            <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'var(--bg2)', color: pctTone(r.pct) }}>{r.pct}%</span>
              <div style={{ fontSize: 11, color: r.left < 0 ? 'var(--danger)' : 'var(--text3)', marginTop: 3 }}>{r.left < 0 ? `${Math.abs(r.left).toFixed(2).replace(/\.?0+$/, '')}h over` : `${r.left.toFixed(2).replace(/\.?0+$/, '')}h left`}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
